import { motion } from 'framer-motion'
import { Icon } from '../common'
import './EntityCard.css'

/**
 * EntityCard - Pokemon card-style display for story entities
 * 
 * @param {string} type - 'character' | 'location' | 'event' | 'shot'
 * @param {string} name - Display name/title
 * @param {string} image - Primary image URL
 * @param {string} subtitle - Secondary text (role, era, date, etc.)
 * @param {Array} details - Array of { label, value } objects for mini grid
 * @param {string} accent - 'cyan' | 'amber' | 'auto' (default: auto based on type)
 * @param {string} tier - 'A' | 'B' | 'C' (default: 'B') - Entity importance level
 * @param {function} onClick - Click handler (opens modal)
 * @param {number} index - For stagger animation delay
 * @param {string} className - Additional CSS classes
 */
export function EntityCard({
  type = 'character',
  name,
  image,
  subtitle,
  details = [],
  accent = 'auto',
  tier = 'B',
  onClick,
  index = 0,
  className = '',
}) {
  // Determine accent color based on type if set to auto
  const resolvedAccent = accent === 'auto' 
    ? (type === 'location' || type === 'shot' ? 'amber' : 'cyan')
    : accent

  // Type labels for badge
  const typeLabels = {
    character: 'Character',
    location: 'Location',
    event: 'Event',
    shot: 'Shot',
  }

  // Type icon names
  const typeIconNames = {
    character: 'node-diamond',
    location: 'locations',
    event: 'node-circle',
    shot: 'shots',
  }

  // Normalize tier to uppercase and validate
  const normalizedTier = ['A', 'B', 'C'].includes(tier?.toUpperCase?.()) 
    ? tier.toUpperCase() 
    : 'B'

  // Framer Motion variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25,
        delay: index * 0.08,
      },
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 20,
      },
    },
    tap: {
      scale: 0.98,
      transition: {
        type: 'spring',
        stiffness: 500,
        damping: 25,
      },
    },
  }

  // Glow animation variants for the border
  const glowVariants = {
    idle: {
      opacity: 0.5,
    },
    hover: {
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  }

  return (
    <motion.article
      className={`entity-card entity-card--${type} entity-card--${resolvedAccent} ${className}`}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      whileTap="tap"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {/* Animated glow border */}
      <motion.span 
        className="entity-card__glow"
        variants={glowVariants}
        initial="idle"
        whileHover="hover"
      />
      
      {/* Inner highlight (top edge) */}
      <span className="entity-card__highlight" />
      
      {/* Card content wrapper */}
      <div className="entity-card__inner">
        {/* Type badge */}
        <div className="entity-card__badge">
          <span className="entity-card__badge-icon"><Icon name={typeIconNames[type]} size={12} /></span>
          <span className="entity-card__badge-text">{typeLabels[type]}</span>
        </div>
        
        {/* Tier badge */}
        <div className={`entity-card__tier entity-card__tier--${normalizedTier.toLowerCase()}`}>
          <span className="entity-card__tier-label">{normalizedTier}</span>
        </div>
        
        {/* Image container */}
        <div className="entity-card__image-container">
          {image ? (
            <img 
              src={image} 
              alt={name}
              className="entity-card__image"
              loading="lazy"
            />
          ) : (
            <div className="entity-card__placeholder">
              <span className="entity-card__placeholder-icon"><Icon name={typeIconNames[type]} size={32} /></span>
            </div>
          )}
          
          {/* Image inner shadow overlay */}
          <div className="entity-card__image-overlay" />
        </div>
        
        {/* Info section */}
        <div className="entity-card__info">
          <h3 className="entity-card__name">{name || 'Unnamed'}</h3>
          {subtitle && (
            <p className="entity-card__subtitle">{subtitle}</p>
          )}
          
          {/* Details mini grid */}
          {details.length > 0 && (
            <div className="entity-card__details">
              {details.slice(0, 4).map((detail, i) => (
                <div key={i} className="entity-card__detail">
                  <span className="entity-card__detail-label">{detail.label}</span>
                  <span className="entity-card__detail-value">{detail.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.article>
  )
}

export default EntityCard

